import { StyleForLHAppHeader } from '@FLAVOR/src/views/components/lh-app-header.css';
import { css, html, nothing, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { classMap } from 'lit/directives/class-map';
import { debounce } from '../../../../ui/debounce';
import { Events } from '../../events/app-events';
import { DepInjMixin, requires } from '../../mixins/dependency';
import { QuestionsService } from '../../services/questions-service';
import { UserService } from '../../services/user-service';
import { eventBus } from '../../utils/event-bus';
import { t } from '../../utils/locale';
import { AnchorIcon, Check, Summary, WizerdStepsIcon } from '../icons/icons';
import { Artline, WizerdDashLine } from '../icons/icons';
import { DeviceAwareComponent } from './device-aware-component';
// import window = Mocha.reporters.Base.window;

type Deps = {
  userService: UserService;
  questionsService: QuestionsService;
};

type SectionInfo = { name: string; active: boolean; };

@requires(UserService.serviceName, QuestionsService.serviceName)
@customElement('lh-app-header')
export class LHAppHeader extends DepInjMixin<Deps>(DeviceAwareComponent) {
  private unsubs: Function[] = [];

  @property({ attribute: 'keyboard-open', reflect: true, type: Boolean })
  public isKeyboardOpen = false;

  @query('#sections')
    sectionsContainer!: HTMLElement;

  @query('#sections-wrapper')
    sectionsContainerWrapper!: HTMLElement;

  @query('#boat-container')
    boatContainer!: HTMLElement;

  @state()
    sections: SectionInfo[] = [];

  @state()
    currentSection: string | undefined;

  @state()
    progress: any = {};

  static get baseStyles() {
    return [
      StyleForLHAppHeader,
      css`
      .icon-check {
        width: 100%;
        position: absolute;
      top: 0.6rem;
      height: 1rem;
      color: var(--c-border-normal);
    }
    /* AI MTA route — only show the logo + customer name (lh-header-line);
       hide the wizard progress sections regardless of device. */
    :host-context(.additional-insured-mta) #sections-wrapper {
      display: none;
    }
    `,
    ];
  };

  static get desktopStyles() {
    return [
      css`
      :host(.section-changing) #sections-wrapper {
        user-select: none;
      }
      #sections-wrapper {
      position: relative;
      transition: transform 550ms var(--transition-page);
      color: var(--c-header-text-not-selected);
    }
    #sections {
      place-content: center;
      flex-flow: column wrap;
      align-items: center;
    }
    #whatsapp-wrapper {
      display: var(--display-none-ondesktop);
    }
    .section-summary-icon {
      color: var(--c-border-normal);
    }
    .section-summary-icon > svg {
      width: 2rem;
      height: auto;
    }
    .section-icon {
      position: relative;
    }
    .section-clickable {
      cursor: pointer;
    }
    arc-progress {
      display: var(--display-none-ondesktop);
      position: absolute;
      top: -6px;
      right: -1.5px;
      color: var(--c-border-normal);
    }
    .section-label{
      max-width: 18.75rem;
      text-overflow: ellipsis;
      color: var(--c-header-text-not-selected);
      padding: 2.5rem 1rem;
      font-size: 2.5rem;
      line-height: 3rem;
      margin-left: 0.3em;
    }
    .section-active{
      font-weight: 700;
      font-size: 2.5rem;
      line-height: 3rem;
      color: var(--c-header-text-selected);
    }
    .section-icon:not(.section-summary-icon) {
      display: inline-block;
      width: 3rem;
      height: 3rem;
      border-radius: 50%;
      border: 0.375rem solid var(--c-border-normal);
      box-sizing: border-box;
      background:  var(--c-bg-normal);
    }
    .section-indicator {
        flex-direction: row;
        flex-basis: max-content;
        width: 65%;
        margin-bottom: 1rem;
        height: 7.625rem;
        justify-content: flex-start;
    }
    #boat-container img {
      transform: var(--artline-wrapper-transform);
      height: 4em;
    }
    #boat-container {
      background:  var(--c-bg-normal);
      transform: translate3d(45px, -30px, 0px);
    }
    .background-dash{
      background-image: url('./assets/dash-line.svg');
      background-position: center bottom;
      background-repeat: no-repeat;
      width: 2px;
      height: 6.5rem;
      position: absolute;
      left: 7.5rem;
      margin-top: 7.25rem;
    }
    .background-stroke{
      background-image: url('./assets/dash_line_stroke.svg');
      background-position: center bottom;
      background-repeat: no-repeat;
      width: 2px;
      height: 6.5rem;
      position: absolute;
      left: 7.5rem;
      margin-top: 7.25rem;
    }
    .background-dash-end{
      background-image: url('./assets/dash-line-end.svg');
      background-position: center bottom;
      background-repeat: no-repeat;
      width: 65px;
      height: 15rem;
      position: absolute;
      left: 7.5rem;
      transform: translateY(6rem);
    }
    * {
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: -moz-none;
    -o-user-select: none;
    user-select: none;
}
    `,
    ];
  };


  static get mobileStyles() {
    return [
      css`
      /* AI MTA route — make lh-header-line take the full header height so the
         logo + customer name centering trick works. */
      :host-context(.additional-insured-mta) {
        display: block;
        height: 100%;
      }
      :host-context(.additional-insured-mta) lh-header-line {
        display: block;
        height: 100%;
      }
      #boat-container {
      background:  var(--c-bg-normal);
    }
    arc-progress {
      display: none;
    }
    .background-dash-start{
        border-bottom: 2px dashed;
        width: 3rem;
        height: 65px;
        top: 1.6rem;
        /*left: 13rem;*/
        position: absolute;
        border-radius: 0 0 30rem;
        transform: var(--artline-wrapper-transform);
    }
    .background-dash{
      width: 4.5rem;
      height: 2px;
      position: absolute;
      margin-left: 7.25rem;
      margin-top: 1.6rem;
      border-bottom: 2px dashed;
      transform: var(--artline-wrapper-transform);
    }
    .background-stroke{
      border-bottom: 2px solid;
      width: 4.5rem;
      height: 2px;
      position: absolute;
      margin-left: 7.25rem;
      margin-top: 1.6rem;
      transform: var(--artline-wrapper-transform);
    }
    .background-dash-end{
        border-bottom: 2px dashed;
        width: 3rem;
        height: 65px;
        margin-left: 6rem;
        top: -5.65rem;
        position: absolute;
        border-radius: 0 0 30rem 0;
    }
    .section-indicator {
      width: 7.2rem;
    }
    #sections {
      justify-content: center;
    }
    `];
  };

  static get rtlStyles() {
    return [
      css`
      #artline-wrapper [data-device="desktop"] {
        margin: 8px -192px;
        transform: rotateY(180deg);
      }`];
  };

  static get ltrStyles() {
    return [
      css`
      #artline-wrapper [data-device="mobile"] {
        transform: rotateY(0deg);
      }`];
  };

  renderContent() {
    const layout = this.deps.questionsService.model.getCurrentSectionLayout();
    const currentSection = this.deps.questionsService.model.getActiveSectionName() || '';
    const isDesktop = this.userDevice === 'desktop';
    return html`
    <lh-header-line></lh-header-line>

    <div id="sections-wrapper" 
    class=${classMap({
    hidden: layout === 'content' || ['S_Summary_Details', 'S_MTA_Coverages'].includes(currentSection),
  })}
    >
        <span
          id="artline-wrapper"
          class=${classMap({
    hidden: this.sections.length <= 1 || !this.currentSection,
  })}
        >
        ${isDesktop ? WizerdDashLine : Artline}
        </span>
        <span
          id="boat-container"
          class=${classMap({
    hidden: this.sections.length <= 1 || !this.currentSection,
  })}
          > ${isDesktop ? WizerdStepsIcon : AnchorIcon}</span
        >
        <div id="sections">
          ${this.sections.length > 1
    ? this.generateSections()
    : ''}
        </div>
      </div>
    `;
  }
  generateSections() {
    const hideSectionsArray = ['S_Boat_OCR'];
    const customerType = this.deps.questionsService.getCustomerType();
    let res = this.sections.filter(section => {
      if (['Type2'].includes(customerType as string) && ['S_Driver'].includes(section.name)) return false;
      if (['Type3'].includes(customerType as string) && ['S_Owner', 'S_Driver', 'S_Pricing'].includes(section.name)) return false;
      return true;
    }).map((section, index, array) => {
      if (hideSectionsArray.includes(section.name)) return null;
      const section_position = (index === 0) ? 'first' : (index === array.length - 1 ? 'last' : '');
      const sec = this.renderSection(section, section_position);
      return sec;
    });
    // remove null values
    res = res.filter((el) => el != null);
    return res;
  }

  renderSection(section: SectionInfo, lastSection: 'last' | 'first' | '' = '') {
    const isSummarySection = section.name === 'S_Summary';
    const isMobile = this.userDevice === 'mobile';
    const sectionProgress = this.progress[section.name];
    const progressValue = sectionProgress ? sectionProgress.done / sectionProgress.done : 0;
    const currentSectionIndex = this.sections.findIndex(s => s.name === this.currentSection);
    const thisSectionIndex = this.sections.findIndex(s => s.name === section.name);
    const isPreviousSection = thisSectionIndex < currentSectionIndex;
    const classes = {
      'section-indicator': true,
      'section-active': section.active || this.currentSection === 'S_Boat_OCR' && section.name === 'S_Boat' && !isMobile,
      'section-clickable': !this.deps.questionsService.model.isSectionAfterLeftSection(section.name),
    };
    const iconClasses = {
      'section-icon': true,
      'section-summary-icon': isSummarySection,
    };
    const titleClasses = {
      'section-label': true,
      'section-active': section.active || this.currentSection === 'S_Boat_OCR' && section.name === 'S_Boat' && !isMobile,
    };
    const backgroundLineClasses = isPreviousSection ? 'background-stroke' : 'background-dash';
    const icon = isSummarySection
      ? Summary
      : html`<arc-progress value="${progressValue}"></arc-progress>`;
    return html`
      <span
        class=${classMap(classes)}
        data-section-name=${section.name}
        @click=${this.handleSectionClick}
      >
        ${lastSection === 'first' ? html`<span class="background-dash-start" ></span>` : nothing}
        <span class=${classMap(iconClasses)}>
          ${icon}${progressValue >= 1 ? Check : ''}
        </span>
        <span class=${classMap(titleClasses)}>${t(section.name)}</span>
        ${lastSection === 'last' ? html`<span class="background-dash-end" ></span>` : html`<span class="${backgroundLineClasses}" ></span>`}
      </span>
    `;
  }

  private handleSectionClick(event: MouseEvent) {
    const target = event.currentTarget as HTMLElement;
    const { sectionName } = target.dataset;
    if (sectionName && !this.deps.questionsService.model.isSectionAfterLeftSection(sectionName)) {
      this.deps.questionsService.model.setActiveSection(sectionName!);
    }
  }


  updated(p: PropertyValues<this>) {

    if (p.has('currentSection')) {
      requestAnimationFrame(() => {
        if (this.currentSection) {
          this.updateComplete.then(() => this.moveBoatToSection());
        }
      });
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.unsubs.push(
      eventBus().on(Events.Type.RefreshHeader, () => this.requestUpdate()),
    );
    this.unsubs.push(
      eventBus().on(Events.Type.ProgressChanged, () => this.checkProgress()),
    );
    this.unsubs.push(
      eventBus().on(Events.Type.SectionChanged, () => {
        this.checkQuestionsState();
        this.updateComplete.then(() => {
          this.moveBoatToSection();
          this.classList.add('section-changing');
          // this.animateSectionPosition();
          setTimeout(() => {
            this.classList.remove('section-changing');
            // @ts-ignore
            //this.sectionsContainerWrapper.style.transform = `scale3d(1, 1, 1) translate3d(0, 0, 0)`;
          }, 100);
        });
      }),
    );
    // @ts-ignore
    this.resizeDone = debounce(this.resizeDone.bind(this), 300);
    this.handleResize = this.handleResize.bind(this);
    this.unsubs.push(
      eventBus().on<boolean>(Events.Type.KeyboardStateChanged, (state) => {
        this.isKeyboardOpen = state;
      }),
    );
    window.addEventListener('resize', this.handleResize);
    this.checkQuestionsState();
    this.checkProgress();
    if (this.currentSection) {
      this.updateComplete.then(() => this.moveBoatToSection());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.handleResize);
    this.unsubs.forEach((f) => f());
  }

  handleResize() {
    this.setAttribute('resizing', '');
    this.moveBoatToSection();
    this.resizeDone(); // throttled
  }

  resizeDone() {
    this.removeAttribute('resizing');
  }

  // private animateSectionPosition() {
  //   this.updateComplete.then(() => {
  //     const sectionElements: HTMLElement[] = Array.from(
  //       this.sectionsContainer.querySelectorAll('.section-indicator')
  //     );
  //     if (sectionElements.length === 0) return;
  //     const idx = sectionElements.findIndex((e) =>
  //       e.classList.contains('section-active')
  //     );
  //     const pct = Math.floor(
  //       ((idx + 1) / (sectionElements.length + 2)) * 100 - 50
  //     );
  //     if (window.innerWidth > 640) {
  //       //animation doesnt work well on desktop this takes care of it
  //       this.sectionsContainerWrapper.style.transform = `scale3d(1.8, 1.8, 1) translate3d(${pct}%, 6rem, 0)`;
  //     } else {
  //       // @ts-ignore
  //       this.sectionsContainerWrapper.style.transform = `scale3d(1.8, 1.8, 1) translate3d(${pct}vw, 6rem, 0)`;
  //     }
  //   });
  // }

  private moveBoatToSection() {
    const { currentSection } = this;
    if (currentSection) {
      const target = this.sectionsContainer.querySelector(
        '.section-active > .section-icon',
      ) as HTMLElement | null;
      if (target) {
        const {
          top: cTop,
          left: cLeft,
        } = this.sectionsContainer.getBoundingClientRect();
        const { top, left, bottom, right } = target.getBoundingClientRect();
        const tLeft = `${-cLeft + (left + right) / 2}px`;
        const tTop = `${-cTop + (top + bottom) / 2}px`;
        this.boatContainer.style.transform = `translate3d(${tLeft}, ${tTop}, 0px)`;
      }
    }
  }

  private checkQuestionsState() {
    this.sections = this.deps.questionsService.model.getSectionsGroup();
    this.currentSection = this.deps.questionsService.model.getActiveSectionName();
  }

  private checkProgress() {
    this.progress = this.deps.questionsService.model.getProgress();
  }
}
